import { MultiSelect, MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectPopup } from 'components/forms/multiselect/MultiSelectPopup';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { useInfiniteAssetDataSource } from 'custom_hooks/data_hooks/data_source/assetsDataSource';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { useMemo } from 'react';
import { uniqueBy } from 'utils/array';
import { buildTooltip } from 'utils/group_hierarchy';

export type AssetsMultiSelectProps = Readonly<{
    corporationId?: string;
    locationId?: string;
    singleSelect?: boolean;
    selectedAssets: MultiSelectOption<string>[];
    onChange: (selections: MultiSelectOption<string>[]) => void;
}>;

export function AssetMultiSelect({
    corporationId,
    locationId,
    singleSelect,
    selectedAssets,
    onChange,
}: AssetsMultiSelectProps) {
    const {
        results: assets,
        hasNext,
        isLoading,
        loadNext,
        filter,
        setFilter,
    } = useInfiniteAssetDataSource({
        name: 'asset-multiselect',
        defaultFilter: {
            nameSearch: '',
            corporationId,
            locationId,
        },
    });

    const assetMultiSelectOptions = useMemo(
        () =>
            assets?.map((asset) => ({
                value: asset.Id,
                title: buildTooltip(asset.Hierarchy, undefined, asset.Name),
            })) ?? [],
        [assets],
    );

    const filterDebounce = useDebounceCallback(250, (value) => {
        setFilter({
            ...filter,
            nameSearch: value,
        });
    });

    return (
        <MultiSelectPopup
            label="Assets"
            searchPlaceholder="Filter assets"
            contentClassName="menu-max-height ml-10"
            popupClassName="modal-popup"
            searchable
            onFilterChange={filterDebounce}
        >
            <InfiniteScroll
                enabled={hasNext ?? false}
                isLoading={isLoading}
                onLoadMore={loadNext}
                className="f-14"
            >
                <MultiSelect
                    options={assetMultiSelectOptions}
                    selectedOptions={selectedAssets}
                    onChange={(event) => {
                        onChange(
                            uniqueBy(
                                [
                                    ...event.selected,
                                    ...selectedAssets.filter((selected) =>
                                        assetMultiSelectOptions.every(
                                            (option) => option.value !== selected.value,
                                        ),
                                    ),
                                ],
                                (selection) => selection.value,
                            ),
                        );
                    }}
                    singleSelect={singleSelect}
                />
            </InfiniteScroll>
        </MultiSelectPopup>
    );
}
